import React from 'react';
import styles from './Terms.module.css';

const Terms = () => {
  return (
    <div className={styles.termsContainer}>
      <h1 className={styles.mainHeading}>
        Terms and Conditions
      </h1>

      <p className={styles.effectiveDate}>
        Effective Date: Baisakh 1, 2081 (April 14, 2024 AD)
      </p>

      <p className={styles.introText}>
        Welcome to YetiDeals. By accessing or using our platform, you agree to
        comply with and be bound by these Terms and Conditions. These terms form
        a legally binding agreement between you and YetiDeals under the
        applicable laws of Nepal.
      </p>

      {/* ===================================================== */}
      {/* 1. ELIGIBILITY */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          1. Eligibility & Account Responsibility
        </h2>

        <p>
          1.1 You must be at least 16 years old to create an account on this
          platform. Users below 18 years of age may require parent or guardian
          involvement for certain transactions as required under applicable laws.
        </p>

        <p>
          1.2 You are responsible for maintaining the confidentiality and
          security of your account credentials. Activities performed under your
          account are considered your responsibility.
        </p>

        <p>
          1.3 YetiDeals reserves the right to suspend, restrict, or terminate
          accounts involved in suspicious activity, fraud, abuse, or violations
          of these terms.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 2. ORDERS & PAYMENTS */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          2. Order & Payment Terms
        </h2>

        <p>
          2.1 Cash on Delivery (COD) is currently the primary payment method
          available on YetiDeals.
        </p>

        <p>
          2.2 Customers are expected to place genuine orders and cooperate with
          delivery procedures.
        </p>

        <p>
          2.3 Repeated misuse of COD services, including repeated refusal of
          genuine orders, may result in account restrictions, suspension, or
          cancellation.
        </p>

        <p>
          2.4 While YetiDeals may support online payment systems in the future,
          currently available payment options will always be displayed clearly
          during checkout.
        </p>

        <p>
          2.5 All transactions are processed in Nepalese Rupees (NPR).
        </p>

        <p>
          2.6 Customers are encouraged to keep appropriate payment amounts ready
          at the time of delivery where COD applies.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 3. DELIVERY */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          3. Delivery Terms
        </h2>

        <p>
          3.1 Delivery timeframes are estimates and may vary depending on
          location, logistics availability, weather conditions, and operational
          circumstances.
        </p>

        <ul className={styles.termsList}>
          <li>Kathmandu Valley: Approximately 1-3 working days</li>
          <li>Other municipalities: Approximately 3-7 working days</li>
          <li>Remote regions: Approximately 7-15 working days</li>
        </ul>

        <p>
          3.2 YetiDeals shall not be held responsible for delays caused by
          events beyond reasonable control, including natural disasters,
          strikes, transportation interruptions, political unrest, weather
          conditions, or internet disruptions.
        </p>

        <p>
          3.3 Delivery times during major festivals or holidays may be longer
          than usual.
        </p>

        <p>
          3.4 If delivery attempts repeatedly fail due to customer
          unavailability or refusal, orders may be cancelled.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 4. RETURNS */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          4. Returns & Refunds Policy
        </h2>

        <p>
          4.1 Customers may request returns or refunds for eligible products
          within the applicable return period shown on the platform.
        </p>

        <p>
          4.2 Returns may be accepted for:
        </p>

        <ul className={styles.termsList}>
          <li>Damaged or defective products</li>
          <li>Incorrect products received</li>
          <li>Missing parts or accessories</li>
          <li>Eligible warranty-related issues</li>
        </ul>

        <p>
          4.3 Products returned should generally be unused and include original
          packaging, accessories, and proof of purchase where applicable.
        </p>

        <p>
          4.4 Certain categories such as digital products, customized items,
          perishable goods, and hygiene-sensitive products may not be eligible
          for return unless required by applicable law.
        </p>

        <p>
          4.5 Approved refunds may be processed through methods determined by
          YetiDeals or the respective vendor.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 5. WARRANTIES */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          5. Product Warranty Terms
        </h2>

        <p>
          5.1 Warranty availability and duration vary depending on the product,
          manufacturer, or vendor.
        </p>

        <ul className={styles.termsList}>
          <li>Electronics: Usually manufacturer warranty applies</li>
          <li>Mobile phones: Generally up to 12 months depending on manufacturer warranty</li>
          <li>Fashion items: Limited manufacturing defect coverage only</li>
          <li>Consumables and accessories may not include warranty coverage</li>
        </ul>

        <p>
          5.2 Warranty does not cover damage caused by misuse, unauthorized
          repairs, accidents, liquid damage, or physical damage unless otherwise
          specified.
        </p>

        <p>
          5.3 Proof of purchase may be required for warranty claims.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 6. PROHIBITED ITEMS */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          6. Prohibited Items
        </h2>

        <p>
          The following items are prohibited from being listed or sold on
          YetiDeals:
        </p>

        <ul className={styles.termsList}>
          <li>Counterfeit or illegal goods</li>
          <li>Narcotic drugs or illegal substances</li>
          <li>Weapons, explosives, or restricted items</li>
          <li>Stolen property or unlawfully obtained goods</li>
          <li>Adult or illegal obscene materials</li>
          <li>Unauthorized prescription medications</li>
          <li>Wildlife products prohibited by law</li>
        </ul>

        <p>
          Violations may result in listing removal, account suspension, or
          reporting to appropriate authorities where necessary.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 7. VENDOR RELATIONSHIP */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          7. Vendor & Platform Relationship
        </h2>

        <p>
          7.1 Vendors are independently responsible for:
        </p>

        <ul className={styles.termsList}>
          <li>Product quality and authenticity</li>
          <li>Accuracy of product descriptions and pricing</li>
          <li>Warranty and after-sales service obligations</li>
          <li>Inventory management and dispatch operations</li>
          <li>Compliance with applicable tax and business laws</li>
        </ul>

        <p>
          7.2 YetiDeals acts as an intermediary ecommerce marketplace platform.
          Vendors remain primarily responsible for their products, warranties,
          and fulfillment obligations.
        </p>

        <p>
          7.3 YetiDeals reserves the right to remove listings or suspend vendors
          violating platform policies.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 8. INTELLECTUAL PROPERTY */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          8. Intellectual Property
        </h2>

        <p>
          8.1 Website content, branding, graphics, logos, designs, and platform
          materials are protected by applicable intellectual property laws.
        </p>

        <p>
          8.2 Unauthorized copying, scraping, reproduction, or commercial use of
          platform content is prohibited without written permission.
        </p>

        <p>
          8.3 Vendors retain ownership of their uploaded content while granting
          YetiDeals permission to display such content on the platform.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 9. PRIVACY */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          9. Privacy & Data Protection
        </h2>

        <p>
          9.1 Personal information is collected and processed in accordance with
          our Privacy Policy.
        </p>

        <p>
          9.2 Customer information may be shared with vendors, logistics
          providers, or authorities only where reasonably necessary for service
          fulfillment, legal compliance, or fraud prevention.
        </p>

        <p>
          9.3 Additional verification information may be requested for
          suspicious or unusually high-value transactions for fraud prevention
          and security purposes.
        </p>

        <p>
          9.4 Platform records and transaction information may be retained for
          operational, legal, accounting, and security purposes.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 10. DISPUTES */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          10. Dispute Resolution
        </h2>

        <p>
          10.1 These terms shall be governed by the applicable laws of Nepal.
        </p>

        <p>
          10.2 Parties are encouraged to attempt good-faith resolution through
          customer support or mediation before initiating formal legal
          proceedings.
        </p>

        <p>
          10.3 Relevant platform records and transaction logs may be used during
          dispute resolution where applicable.
        </p>

        <p>
          10.4 Any disputes shall be subject to the applicable courts of Nepal,
          with preference to courts located in Jhapa District.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 11. FORCE MAJEURE */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          11. Amendments & Force Majeure
        </h2>

        <p>
          11.1 YetiDeals may modify these Terms and Conditions from time to
          time. Updated versions will be published on the platform.
        </p>

        <p>
          11.2 Continued use of the platform after updates become effective
          constitutes acceptance of revised terms.
        </p>

        <p>
          11.3 YetiDeals shall not be responsible for service interruptions,
          delays, or failures caused by events beyond reasonable control,
          including natural disasters, strikes, internet outages, pandemics,
          governmental actions, or infrastructure failures.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 12. LIABILITY */}
      {/* ===================================================== */}
      <div className={styles.termsSection}>
        <h2 className={styles.termsHeading}>
          12. Limitation of Liability
        </h2>

        <p>
          12.1 To the extent permitted by law, YetiDeals’ liability relating to
          a specific order shall generally not exceed the amount paid for that
          order.
        </p>

        <p>
          12.2 YetiDeals shall not be responsible for indirect or consequential
          damages arising from platform use where such limitations are permitted
          under applicable law.
        </p>

        <p>
          12.3 Customers are encouraged to inspect products upon delivery and
          promptly report issues in accordance with the applicable return policy.
        </p>
      </div>

      {/* ===================================================== */}
      {/* CONTACT */}
      {/* ===================================================== */}
      <div className={styles.contactSection}>
        <h2 className={styles.termsHeading}>
          Contact Information
        </h2>

        <p>
          For questions, complaints, or support regarding these Terms and
          Conditions, please contact:
        </p>

        <p>
          <strong>YetiDeals Support & Grievance Team</strong>
        </p>

        <p>
          <strong>Email:</strong> yetideals.np@gmail.com
        </p>

        <p>
          <strong>Phone:</strong> +977-9815014878
        </p>

        <p>
          <strong>Address:</strong> Birtamode-4, Jhapa, Nepal
        </p>

        <p>
          We aim to respond to inquiries as promptly as reasonably possible.
        </p>
      </div>
    </div>
  );
};

export default Terms;
